import { useGetContext } from "@onefront/forrestjs-react-root";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../service-auth";
import { useAxios } from "../../service-axios";

/**
 * The default error handler tries to intercept an unauthorized server response
 * in order to trigger an ApplicationToken refresh action and replay the request
 * @param {*} error
 * @returns
 */
const DEFAULT_ERROR_HANDLER = (error) =>
  error.response && error.response.status === 401;

export const useRequest = (receivedConfig = {}) => {
  const {
    url: receivedUrl,
    lazy: receivedLazy,
    onResponse,
    pollingInterval = 0,
    onError = DEFAULT_ERROR_HANDLER,
    stateless,
    ...axiosConfig
  } = receivedConfig;

  const target = receivedUrl || "";
  const lazy = target ? receivedLazy : true;

  const accessTokenHeaderKey = useGetContext("one.rest.header.accessToken.key");
  const accessTokenHeaderValue = useGetContext(
    "one.rest.header.accessToken.value"
  );
  const headersPropName = useGetContext("one.rest.header.property.name");
  const urlPropName = useGetContext("one.rest.url.property.name");
  const { token, refreshToken } = useAuth();
  const { axios, buildConfig } = useAxios(axiosConfig);
  const refreshedTokenPromise = useRef();

  const [state, setState] = useState({
    loading: lazy ? false : true,
    response: null,
    error: null,
    isPolling: false,
    pollingCount: 0,
    isFirstRequest: false,
    hasLoaded: false
  });

  const [timeoutId, setTimeoutId] = useState();

  // This is a short term solution, performances may suck due to the serialization.
  // NOTE: ChatGPT agrees! (almost like Chuck Norris, but not quite as much)
  const __dependencies = [
    token,
    // BUG: sorting by keys cause nested objects to empty (like headers)
    // JSON.stringify(receivedConfig, Object.keys(receivedConfig).sort())
    JSON.stringify(receivedConfig)
  ];

  const stopPolling = () => {
    setState((state) => ({
      ...state,
      isPolling: false,
      pollingCount: 0,
      isFirstRequest: false,
      hasLoaded: false
    }));
    clearTimeout(timeoutId);
  };

  const startPolling = (interval) => {
    if (state.isPolling) return;

    if (!Number.isInteger(interval)) {
      interval = pollingInterval;
    }

    execLoop(interval);
  };

  const execLoop = (interval) => {
    setState((state) => ({
      ...state,
      isPolling: true,
      isFirstRequest: state.pollingCount < 1,
      hasLoaded: false
    }));
    fetch()
      .then(() => {
        setState((state) => ({
          ...state,
          pollingCount: state.pollingCount + 1,
          isFirstRequest: state.pollingCount <= 1,
          hasLoaded: true
        }));
        if (!!interval) {
          setTimeoutId(setTimeout(() => execLoop(interval), interval));
        }
      })
      .catch(() => {
        setState((state) => ({
          ...state,
          isPolling: false,
          pollingCount: 0,
          isFirstRequest: false,
          hasLoaded: false
        }));
      });
  };

  const buildAuthorization = (
    headers,
    token,
    tokenHeaderKey,
    tokenHeaderValue
  ) => {
    let requestHeaders = {};
    if (!headers.hasOwnProperty(tokenHeaderKey)) {
      requestHeaders = {
        [tokenHeaderKey]: tokenHeaderValue.replace("{TOKEN}", token)
      };
    }
    return requestHeaders;
  };

  const getHeaders = (fetchConfig = {}, receivedConfig = {}) =>
    fetchConfig.hasOwnProperty(headersPropName)
      ? fetchConfig?.headers
      : receivedConfig?.headers;

  const setTarget = (fetchConfig = {}, target = "") =>
    fetchConfig.hasOwnProperty(urlPropName) ? fetchConfig[urlPropName] : target;

  const buildConfiguration = (fetchConfig, validToken) => {
    const authorizationHeaders = getHeaders(fetchConfig, receivedConfig);
    const authorization = buildAuthorization(
      authorizationHeaders ?? {},
      validToken,
      accessTokenHeaderKey,
      accessTokenHeaderValue
    );

    const config = buildConfig(fetchConfig, {
      url: setTarget(fetchConfig, target),
      headers: {
        ...authorization,
        ...fetchConfig?.headers
      }
    });
    return config;
  };

  const setUpdateState = (fetchConfig) => {
    return !(!!fetchConfig.hasOwnProperty("stateless")
      ? fetchConfig?.stateless
      : receivedConfig?.stateless);
  };

  const fetch = useCallback(async (fetchConfig = {}) => {
    const updateState = setUpdateState(fetchConfig);
    updateState &&
      setState((state) => ({ ...state, loading: true, error: null }));
    try {
      const config = buildConfiguration(fetchConfig, token);
      const response = await axios.request(config);

      onResponse && (await onResponse(response));
      updateState &&
        setState((state) => ({
          ...state,
          loading: false,
          response,
          hasLoaded: true
        }));
      return response;
    } catch (error) {
      // Try to catch an error that should trigger an automated session refresh
      if (onError && (await onError(error))) {
        try {
          // Refresh the AccessToken
          //const { token: refreshedToken } = await refreshToken();

          if (!refreshedTokenPromise.current) {
            refreshedTokenPromise.current = refreshToken();
          }
          const { token: refreshedToken } = await refreshedTokenPromise.current;

          refreshedTokenPromise.current = null;

          // Replay the original request but with the updated
          // ApplicationToken
          try {
            const config = buildConfiguration(fetchConfig, refreshedToken);
            const response = await axios.request(config);

            onResponse && (await onResponse(response));
            updateState &&
              setState((state) => ({
                ...state,
                loading: false,
                response,
                hasLoaded: true
              }));
            return response;
            // This is a genuine requst error and should be managed by
            // the requester code.
          } catch (error2) {
            updateState &&
              setState((state) => ({
                ...state,
                response: null,
                loading: false,
                error: error2,
                hasLoaded: false
              }));
            return Promise.reject(error2);
          }

          // This catches the SessionRefresh error and should produce
          // a terminal error that simply kick the user out.
          // This error should be managed by the box to block the UI
        } catch (error1) {
          updateState &&
            setState((state) => ({
              ...state,
              response: null,
              loading: false,
              error: error1,
              hasLoaded: false
            }));
          return Promise.reject(error1);
        }

        // Manage an unexpected error :
        // (not session ender error)
      } else {
        updateState &&
          setState((state) => ({
            ...state,
            response: null,
            loading: false,
            error,
            hasLoaded: false
          }));
        return Promise.reject(error);
      }
    }
  }, __dependencies);

  useEffect(() => {
    if (lazy) return;
    if (!!pollingInterval) {
      startPolling();
    } else {
      fetch().catch(() => {});
    }
  }, __dependencies);

  return {
    ...state,
    data: state.response ? state.response.data : null,
    fetch,
    startPolling,
    stopPolling
  };
};
